import {
  EnergyFlowEmeterIntervalModel,
  EnergyFlowIntervalModel,
} from '@core/models/topological-telemetry.model';
import { PhaseCount } from '@core/models';

// Readings for a valid, non-missing emeter; null otherwise (renders as a gap in the line).
export function readings(
  emeter: EnergyFlowEmeterIntervalModel | undefined,
  field: keyof EnergyFlowEmeterIntervalModel,
): number[] | null {
  if (!emeter || emeter.quality === 'missing') return null;
  const values = emeter[field];
  return Array.isArray(values) ? (values as number[]) : null;
}

export function hasReadings(
  emeter: EnergyFlowEmeterIntervalModel | undefined,
  field: keyof EnergyFlowEmeterIntervalModel,
): boolean {
  const values = readings(emeter, field);
  return !!values && values.some(v => v != null);
}

export function phaseCount(
  intervals: EnergyFlowIntervalModel[],
  getEmeter: (interval: EnergyFlowIntervalModel) => EnergyFlowEmeterIntervalModel | undefined,
  field: keyof EnergyFlowEmeterIntervalModel,
): number {
  let count = 0;
  for (const interval of intervals) {
    const values = readings(getEmeter(interval), field);
    if (values) count = Math.max(count, values.length);
  }
  return count;
}

// Which array index is the actually-connected phase. Auto-detection (RDT-589) only persists
// the phase *count*, never which specific phase is live, so this is derived directly from the
// telemetry itself (first index with a non-zero reading) rather than a backend field - the
// ticket only forbids "always assume L1", it doesn't require the backend to be the source.
//
// Detected from power specifically, not the metric currently being charted: on a real
// 1-phase installation, voltage is often sensed on all three lines even though only one
// carries load, so a per-field "first non-zero" search would pick a different phase for the
// voltage chart than for the power chart of the same device. Power reliably narrows to the
// phase that's actually connected; that same index is then reused for whichever metric is
// charted, falling back to `field` only if a source genuinely has no power readings.
export function activePhaseIndex(
  intervals: EnergyFlowIntervalModel[],
  getEmeter: (interval: EnergyFlowIntervalModel) => EnergyFlowEmeterIntervalModel | undefined,
  field: keyof EnergyFlowEmeterIntervalModel,
  count: number,
): number {
  for (const interval of intervals) {
    const emeter = getEmeter(interval);
    const values = readings(emeter, 'activePowerInW') ?? readings(emeter, field);
    const index = values?.findIndex(value => value != null && value !== 0);
    if (index != null && index >= 0) return Math.min(index, count - 1);
  }
  return 0;
}

// RDT-589: which phase indices to render for this source. Normally every index the
// telemetry array reports; clamped to the single active phase when phaseCountOverride is '1'.
export function phaseIndices(
  intervals: EnergyFlowIntervalModel[],
  getEmeter: (interval: EnergyFlowIntervalModel) => EnergyFlowEmeterIntervalModel | undefined,
  field: keyof EnergyFlowEmeterIntervalModel,
  phaseCountOverride: PhaseCount | undefined,
): number[] {
  const count = phaseCount(intervals, getEmeter, field);
  if (count === 0) return [];
  if (phaseCountOverride === '1') {
    return [activePhaseIndex(intervals, getEmeter, field, count)];
  }
  return Array.from({ length: count }, (_, i) => i);
}
